import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface DeleteClientModalProps {
  count: number;
  onConfirm: (dontShowAgain: boolean) => void;
  onCancel: () => void;
  /** When true, hides the "Don't show again" checkbox */
  hideDontShowAgain?: boolean;
}

export function DeleteClientModal({ count, onConfirm, onCancel, hideDontShowAgain }: DeleteClientModalProps) {
  const { colors } = useTheme();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <View style={[styles.header, { backgroundColor: colors.red.background, borderColor: colors.red.border }]}>
        <MaterialCommunityIcons name="alert-circle" size={24} color={colors.red.text} />
        <ThemedText type="subtitle" style={{ color: colors.red.text, marginLeft: 8 }}>
          Delete Client{count !== 1 ? "s" : ""}
        </ThemedText>
      </View>

      <View style={styles.body}>
        <ThemedText style={styles.message}>
          You&apos;re about to permanently delete <ThemedText style={styles.count}>{count}</ThemedText> client
          {count !== 1 ? "s" : ""}.
        </ThemedText>

        <View style={[styles.warningBox, { backgroundColor: colors.red.background, borderColor: colors.red.border }]}>
          <MaterialCommunityIcons name="trash-can-outline" size={20} color={colors.red.text} />
          <ThemedText style={[styles.warningText, { color: colors.red.text }]}>
            This action cannot be undone. All client data will be permanently removed.
          </ThemedText>
        </View>

        {!hideDontShowAgain && (
          <Pressable style={styles.checkboxRow} onPress={() => setDontShowAgain(!dontShowAgain)}>
            <MaterialCommunityIcons
              name={dontShowAgain ? "checkbox-marked" : "checkbox-blank-outline"}
              size={22}
              color={dontShowAgain ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={[styles.checkboxLabel, { color: colors.textSecondary }]}>
              Don&apos;t show this message again
            </ThemedText>
          </Pressable>
        )}
      </View>

      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Delete"
          icon="trash-can"
          onPress={() => onConfirm(dontShowAgain)}
          style={[styles.deleteButton, { backgroundColor: colors.red.background, borderColor: colors.red.border }]}
          textStyle={{ color: colors.red.text }}
          iconStyle={{ color: colors.red.text }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: 420, borderRadius: 12, borderWidth: 1, overflow: "hidden" },
  header: { flexDirection: "row", alignItems: "center", padding: 16, borderBottomWidth: 1 },
  body: { padding: 16, gap: 16 },
  message: { fontSize: 15, lineHeight: 22 },
  count: { fontWeight: "700", fontSize: 16 },
  warningBox: { flexDirection: "row", alignItems: "center", padding: 12, borderRadius: 8, borderWidth: 1, gap: 10 },
  warningText: { fontSize: 14, fontWeight: "500", flex: 1 },
  checkboxRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  checkboxLabel: { fontSize: 13 },
  footer: { flexDirection: "row", justifyContent: "flex-end", gap: 8, padding: 12 },
  cancelButton: { height: 36, paddingHorizontal: 16, backgroundColor: "transparent", borderWidth: 1 },
  deleteButton: { height: 36, paddingHorizontal: 16, borderWidth: 1 },
});
